import { cn } from "@/lib/cn";
import type { CSSProperties } from "react";

export interface SparklineProps {
  /** Array of numeric values to render as bars */
  values: number[];
  /** Height of the sparkline container in px */
  height?: number;
  /** Bar color — defaults to magenta neon */
  color?: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * Magenta bar sparkline typically rendered beneath KPI tile values.
 * Each bar's height is proportional to the max value in the dataset.
 */
export function Sparkline({
  values,
  height = 56,
  color = "var(--iris-neon-magenta)",
  className,
  style,
}: SparklineProps) {
  const max = Math.max(...values, 0.001);
  return (
    <div
      className={cn("iris-sparkline", className)}
      style={{
        height,
        display: "flex",
        alignItems: "flex-end",
        gap: 2,
        ...style,
      }}
    >
      {values.map((v, i) => (
        <div
          key={`bar-${i}-${v}`}
          style={{
            flex: 1,
            height: `${Math.max(4, (v / max) * 100)}%`,
            background: color,
            borderRadius: 2,
            boxShadow: "0 0 6px rgba(255,46,166,0.45)",
            opacity: 0.85 + 0.15 * (v / max),
          }}
        />
      ))}
    </div>
  );
}

/**
 * Generate deterministic pseudo-random sparkline data.
 * @param n - Number of bars to generate
 * @param seed - Seed for deterministic output
 * @returns Array of values between 0.25 and 1.0
 */
export function makeSpark(n = 40, seed = 1): number[] {
  const out: number[] = [];
  let s = seed;
  for (let i = 0; i < n; i++) {
    s = (s * 9301 + 49297) % 233280;
    out.push(0.25 + (s / 233280) * 0.75);
  }
  return out;
}
